import { fetchArchive, mapPost } from "../lib/wordpress";
import { mergeAuthorPosts } from "../data/stories";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Link } from "./CanonicalLink";
import { Newspaper } from "lucide-react";
import TopBar from "./TopBar";
import Masthead from "./Masthead";
import SponsoredStrip from "./SponsoredStrip";
import Footer from "./Footer";
import Reveal from "./Reveal";
import HeroSection from "./HeroSection";
import { titleCaseHeadline } from "../utils/titleCase";
import { SITE, STORIES, CATEGORY_LABELS, getCategoryStories } from "../data/stories";

/* Layout mirrors the live standard.gm category page: a full-width lead card,
   then a dense multi-column grid of compact cards (image + kicker + title + date,
   no excerpts), kept under the site's own display typography. */
const FIRST_PAGE_SIZE = 13; // five hero stories + eight grid cards
const REST_PAGE_SIZE = 8;

const StoryCard = ({ story, testid }) => (
  <article data-testid={testid} className="group h-full">
    <Link to={`/${story.id}`} className="block h-full">
      {story.img && (
        <div className="img-frame overflow-hidden aspect-[16/10] bg-[var(--card-muted)]">
          <img
            src={story.img}
            alt={story.imgAlt || story.title}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <p className="mt-3.5 flex items-center justify-between gap-3">
        <span className="text-[11px] text-[var(--faint)] whitespace-nowrap">{story.date}</span>
      </p>
      <h3 className="mt-1.5 font-display text-lg leading-snug font-semibold group-hover:text-[var(--accent)] transition-colors duration-300">
        {titleCaseHeadline(story.title)}
      </h3>
    </Link>
  </article>
);

export const CategoryPage = () => {
  const { id: routeId } = useParams();
  const id = routeId || "all-news";
  const label = CATEGORY_LABELS[id] || id;
  const [remote, setRemote] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveError, setArchiveError] = useState(false);
  const stories = remote?.id === id ? remote.posts : getCategoryStories(id);
  const [page, setPage] = useState(1);
  const listTopRef = useRef(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    setPage(1);
  }, [id]);

  const totalCount = remote?.id === id ? remote.total : stories.length;
  const totalPages = totalCount <= FIRST_PAGE_SIZE ? 1 : 1 + Math.ceil((totalCount - FIRST_PAGE_SIZE) / REST_PAGE_SIZE);
  useEffect(() => {
    let active = true;
    setArchiveLoading(true); setArchiveError(false);
    fetchArchive({category:id,offset:page === 1 ? 0 : FIRST_PAGE_SIZE + (page-2)*REST_PAGE_SIZE,perPage:page === 1 ? FIRST_PAGE_SIZE : REST_PAGE_SIZE})
      .then(result => { if(active) { mergeAuthorPosts(result.posts); setRemote({id,total:result.total,posts:result.posts.map(mapPost)}); } })
      .catch(() => { if(active)setArchiveError(true); }).finally(() => {if(active)setArchiveLoading(false);});
    return () => {active=false;};
  }, [id,page]);
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const paginationItems = [];
  let previous = 0;
  for (let number = 1; number <= totalPages; number++) {
    if (totalPages <= 5 || number === 1 || number === totalPages || Math.abs(number - safePage) <= 1) {
      if (previous && number - previous > 1) paginationItems.push(`gap-${number}`);
      paginationItems.push(number);
      previous = number;
    }
  }
  const pageItems =
    safePage === 1
      ? stories.slice(Math.min(5, stories.length), FIRST_PAGE_SIZE)
      : remote?.id === id ? stories : stories.slice(FIRST_PAGE_SIZE + (safePage-2)*REST_PAGE_SIZE,FIRST_PAGE_SIZE + (safePage-1)*REST_PAGE_SIZE);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    const el = listTopRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 72;
    const lenis = window.__lenis;
    if (lenis) lenis.scrollTo(top);
    else window.scrollTo({ top, behavior: "smooth" });
  }, [safePage, id]);

  const goToPage = (p) => {
    if (p < 1 || p > totalPages || p === safePage) return;
    setPage(p);
  };



  return (
    <div data-testid="category-page">
      <TopBar />
      <Masthead />

      <main>
        <section className="bg-[var(--bg)]">
          <div className="mx-auto max-w-[1440px] px-4 sm:px-6 pt-10 sm:pt-14">
            <Reveal>
              <div className="rounded-[4px] overflow-hidden border-t-[3px] border-[#cf0a0a] bg-[var(--bg-soft)] px-5 sm:px-7 py-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                  <h1
                    data-testid="category-title"
                    className="font-display text-3xl sm:text-4xl font-semibold"
                  >
                    {label}
                  </h1>
                  <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-[var(--faint)]" data-testid="category-count">
                  {id === "advertisement"
                    ? "Partner placements"
                    : `${totalCount} ${totalCount === 1 ? "story" : "stories"}`}
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {id === "advertisement" ? (
          <div className="mx-auto max-w-[1440px] px-4 sm:px-6 pb-4">
            <p className="mt-8 text-sm leading-relaxed text-[var(--muted)] max-w-2xl" data-testid="advertisement-note">
              This space connects Gambian businesses with our readers across the
              country and the diaspora. Every placement below is a paid partner
              placement. To advertise, write to {SITE.email}.
            </p>
          </div>
        ) : null}

        {stories.length === 0 ? (
          <div className="mx-auto max-w-[1440px] px-4 sm:px-6 pb-4">
            <div
              className="mt-8 border border-[var(--line)] bg-[var(--bg-soft)] px-6 py-14 text-center"
              data-testid="category-empty-state"
            >
              <Newspaper size={26} className="mx-auto text-[var(--faint)]" />
              <h2 className="mt-4 font-display text-2xl font-semibold">
                No stories in {label} yet
              </h2>
              <p className="mt-2 text-sm text-[var(--muted)]">
                Our reporters are on it — check back soon.
              </p>
            </div>
          </div>
        ) : (
          <div ref={listTopRef}>
            {archiveLoading && <p role="status" className="text-center py-3 text-sm">Loading stories…</p>}
            {archiveError && <p role="alert" className="text-center py-3 text-sm">Unable to refresh the archive. Please try again.</p>}
            {safePage === 1 && <HeroSection categoryStories={stories} adPanel showSecondary={false} />}

            {pageItems.length > 0 && (
              <section className="mx-auto max-w-[1440px] px-4 sm:px-6 pt-10 pb-4">
                <div
                  key={safePage}
                  className="border-t-2 border-[var(--divider)] pt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10"
                >
                  {pageItems.map((story, i) => (
                    <Reveal key={story.id} delay={(i % 4) * 0.06} className="h-full">
                      <StoryCard story={story} testid={`category-page-card-${story.id}`} />
                    </Reveal>
                  ))}
                </div>
              </section>
            )}

            {totalPages > 1 && (
              <section className="mx-auto max-w-[1440px] px-4 sm:px-6 pt-4 pb-16">
                <nav
                  data-testid="category-pagination"
                  aria-label={`${label} pages`}
                  className="flex flex-wrap items-center justify-center gap-2 border-t border-[var(--line)] pt-8"
                >
                  <button
                    type="button"
                    onClick={() => goToPage(safePage - 1)}
                    disabled={safePage === 1}
                    data-testid="category-page-prev"
                    className="h-10 px-4 border border-[var(--line)] text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--muted)] transition-colors duration-300 hover:border-[#cf0a0a] hover:text-[#cf0a0a] disabled:opacity-40 disabled:pointer-events-none"
                  >
                    ← Prev
                  </button>
                  {paginationItems.map((n) => typeof n === "string" ? <span key={n} aria-hidden="true" className="px-1 text-[var(--faint)]">…</span> : (
                    <button
                      key={n}
                      type="button"
                      onClick={() => goToPage(n)}
                      aria-current={n === safePage ? "page" : undefined}
                      data-testid={`category-page-num-${n}`}
                      className={`w-10 h-10 border text-[13px] font-bold transition-colors duration-300 ${
                        n === safePage
                          ? "bg-[#cf0a0a] border-[#cf0a0a] text-white"
                          : "border-[var(--line)] text-[var(--muted)] hover:border-[#cf0a0a] hover:text-[#cf0a0a]"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => goToPage(safePage + 1)}
                    disabled={safePage === totalPages}
                    data-testid="category-page-next"
                    className="h-10 px-4 border border-[var(--line)] text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--muted)] transition-colors duration-300 hover:border-[#cf0a0a] hover:text-[#cf0a0a] disabled:opacity-40 disabled:pointer-events-none"
                  >
                    Next →
                  </button>
                  <span
                    data-testid="category-page-status"
                    className="w-full text-center mt-3 text-[10px] uppercase tracking-[0.2em] text-[var(--faint)]"
                  >
                    Page {safePage} of {totalPages}
                  </span>
                </nav>
              </section>
            )}
          </div>
        )}


      </main>
      <Footer />
    </div>
  );
};

export default CategoryPage;
